/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import Collapsible from 'react-collapsible';
import get from 'lodash/get';
import { cloneDeep } from 'lodash';
import Modal from '../../../../ui/Modal';
import DotComponent from '../../../../ui/DotComponent';
import { processAnalyticsResultsFilterData } from '../../../../../utils/analytics';
import { SafePureComponent } from '../../../../../utils/SafePureComponent';
import { Checkbox, Input } from '../../../../ui/Form/';
import { RICH_FILTERING as dict } from '../../../../../utils/dictionarykeys';
import {
  BRAND_PARAM,
  SORT_BY_QUERY_PARAM,
  MIN_PARAM,
  MAX_PARAM,
  SOLUTIONS,
  SORT_BY_LOW_TO_HIGH,
} from '../../../../../utils/constants';
import { withConsumer } from '../../../../../contexts';
import { RichFilteringConsumer } from '../../../../../contexts/RichFilteringContext';
import createButtonCss from '../../../../ui/Button/Button';
import { buttonReset } from '../../../../../styles/Global';
import { getDecodedQueryParams } from '../../../../../utils/url';
import * as styles from './index.styles';

const brandCollapsibleOptions = {
  open: false,
  tabIndex: 0,
  transitionTime: 100,
  triggerTagName: 'button',
};

const keyCodes = {
  negative: 189,
  e: 69,
};

const Brand = props => {
  const { onChangeBrand, defaultChecked, brandsNumbers } = props;
  const { Label, Description, RawValue } = props.fields;

  return (
    <React.Fragment>
      <Checkbox
        value={defaultChecked}
        css={styles.checkboxLabel}
        id={RawValue.value}
        label={Label.value}
        onChangeProp={onChangeBrand}
        render={() => {
          const brandQuantity = brandsNumbers[RawValue.value];
          const number =
            brandQuantity >= 10 ? brandQuantity : `0${brandQuantity || 0}`;

          return <span css={styles.number}>{number}</span>;
        }}
      />
      <p css={styles.description}>{Description.value}</p>
    </React.Fragment>
  );
};

const SortItem = props => {
  const { isActive, onClickSort } = props;
  const { Label, RawValue } = props.fields;

  return (
    <button
      css={[buttonReset, styles.sortItemButton]}
      data-id={RawValue.value}
      onClick={onClickSort}
    >
      {isActive && (
        <span css={styles.checkIcon} className="icon-checkmark"></span>
      )}

      <p css={styles.sortText(isActive)}>{Label.value}</p>
    </button>
  );
};

const Ctas = ({ onClickClear, onClickShowResults, t }) => {
  return (
    <div css={styles.ctas}>
      <button
        onClick={onClickShowResults}
        css={[createButtonCss(), styles.resultsButton]}
      >
        {t(dict.SHOW_RESULTS)}
      </button>
      <div css={styles.resetButtonWrapper}>
        <button onClick={onClickClear} css={styles.resetButton}>
          <span css={styles.resetText}>{t(dict.CLEAR)}</span>
        </button>
      </div>
    </div>
  );
};

export class FiltersModal extends SafePureComponent {
  state = {
    brands: [],
    sort: SORT_BY_LOW_TO_HIGH,
    min: '',
    max: '',
  };

  onChangeBrand = event => {
    const { checked, name } = event.target;
    const brands = cloneDeep(this.state.brands);
    const nameIndexInBrands = brands.indexOf(name);
    if (checked) {
      brands.push(name);
    } else {
      brands.splice(nameIndexInBrands, 1);
    }

    this.setState({
      brands: brands,
    });
  };

  onClickClear = () => {
    const { selectedFilters } = this.props;
    selectedFilters(false);

    this.setState({
      brands: [],
      sort: SORT_BY_LOW_TO_HIGH,
      min: '',
      max: '',
    });
  };

  buildBrandsUrl = () => {
    const { brands } = this.state;
    const brandsUrl = brands.join('|');

    return brandsUrl;
  };

  decodeBrandsUrl = () => {
    const params = getDecodedQueryParams();
    const brands = params[BRAND_PARAM];

    return brands ? brands.split('|') : [];
  };

  buildFiltersParams = () => {
    const { sort, min, max } = this.state;

    const queryParams = {
      [SORT_BY_QUERY_PARAM]: sort,
      [BRAND_PARAM]: this.buildBrandsUrl(),
      [MIN_PARAM]: `${min}`,
      [MAX_PARAM]: `${max}`,
    };

    return queryParams;
  };

  isAFilterSelected = () => {
    const { min, max, brands, sort } = this.state;

    const { selectedFilters } = this.props;

    if (brands.length > 0 || min || max || sort !== SORT_BY_LOW_TO_HIGH) {
      selectedFilters(true);
      return;
    }

    selectedFilters(false);
  };

  onClickShowResults = () => {
    const { closeModal } = this.props;

    const { replaceWindowUrl, filterLocations } = get(
      this.props,
      'richFilteringContext.actions',
      {},
    );
    const queryParams = this.buildFiltersParams();

    this.isAFilterSelected();
    closeModal();
    replaceWindowUrl(queryParams);
    filterLocations(this.state);

    processAnalyticsResultsFilterData(queryParams);
  };

  onClickSort = event => {
    const { dataset } = event.target;

    this.setState({
      sort: dataset.id,
    });
  };

  getDefaultUrlParams = () => {
    const params = getDecodedQueryParams();
    const sort = params[SORT_BY_QUERY_PARAM] || SORT_BY_LOW_TO_HIGH;
    const brands = this.decodeBrandsUrl();
    const min = Number(params[MIN_PARAM]);
    const max = Number(params[MAX_PARAM]);

    const queryParams = {
      [BRAND_PARAM]: brands,
      [SORT_BY_QUERY_PARAM]: sort,
      [MIN_PARAM]: min || '',
      [MAX_PARAM]: max || '',
    };

    this.setState(queryParams, () => {
      this.isAFilterSelected();
    });
  };

  componentDidMount() {
    const { clearModalState } = this.props;
    this.getDefaultUrlParams();
    clearModalState(this.onClickClear);
  }

  closeModal = () => {
    const { closeModal } = this.props;
    this.getDefaultUrlParams();
    closeModal();
  };

  buildSorts = () => {
    const { filters } = this.props;
    const { sort } = this.state;
    const sortItems = get(filters, 'Sorting.items', []);

    return sortItems.map(item => {
      const isActive = item.RawValue.value === sort;
      return (
        <SortItem
          isActive={isActive}
          fields={item}
          onClickSort={this.onClickSort}
          key={item.RawValue.value}
        ></SortItem>
      );
    });
  };

  buildBrands = () => {
    const { filters } = this.props;
    const { brands } = this.state;
    const brandItems = get(filters, 'Brands.items', []);
    const { brandsNumbers } = get(this.props, 'richFilteringContext.state', {});

    return brandItems.map(item => {
      const brandValue = get(item, 'fields.RawValue.value', '');
      const defaultChecked = brands.some(brand => brand === brandValue);

      return (
        <section css={styles.brandWrapper} key={brandValue}>
          <Brand
            brandsNumbers={brandsNumbers}
            defaultChecked={defaultChecked}
            fields={item.fields}
            onChangeBrand={this.onChangeBrand}
          />
        </section>
      );
    });
  };

  onChangeRange = event => {
    const { id, value } = event.target;

    this.setState({ [id]: value });
  };

  onKeyDownRange = event => {
    const { keyCode } = event;

    if (keyCode === keyCodes.e || keyCode === keyCodes.negative) {
      event.preventDefault();
    }
  };

  buildRange = () => {
    const { t } = this.props;
    const { min, max } = this.state;
    const { params } = get(this.props, 'richFilteringContext.state', {});
    const isAMeetingRoom =
      params.ws === SOLUTIONS.MEETING_ROOM.replace('/', '');

    return (
      <section>
        <div css={styles.inputsContainer}>
          <div css={styles.inputs}>
            <Input
              css={styles.inputNumber}
              id="min"
              inputProps={{ onKeyDown: this.onKeyDownRange, min: '0' }}
              customFieldCss={styles.inputWidth}
              placeholder={t(dict.MIN)}
              type="number"
              onChange={this.onChangeRange}
              value={min}
            />
            <span css={styles.rangeTextTo}>{t(dict.TO)}</span>
            <Input
              css={styles.inputNumber}
              customFieldCss={styles.inputWidth}
              id="max"
              inputProps={{ onKeyDown: this.onKeyDownRange, min: '0' }}
              placeholder={t(dict.MAX)}
              type="number"
              onChange={this.onChangeRange}
              value={max}
            />
          </div>
          <span css={styles.rangeTextPer}>
            {isAMeetingRoom ? t(dict.PPPH) : t(dict.PPPM)}
          </span>
        </div>
        <p css={styles.rangeInfo}>
          {isAMeetingRoom
            ? t(dict.MESSAGE_PRICE_PER_HOUR)
            : t(dict.MESSAGE_PRICE_PER_MONTH)}
        </p>
      </section>
    );
  };

  safeRender() {
    const { isModalOpen, t } = this.props;

    return (
      <Modal
        css={styles.modalContent}
        customCloseButtonCss={styles.closeModalButton}
        isOpen={isModalOpen}
        onRequestClose={this.closeModal}
        overlayProp={styles.modalOverlay}
      >
        <div css={styles.wrapper}>
          <h2 css={styles.title}>
            {t(dict.FILTERS)}
            <DotComponent />
          </h2>
          <Collapsible {...brandCollapsibleOptions} trigger={t(dict.SORT_BY)}>
            {this.buildSorts()}
          </Collapsible>
          <Collapsible
            {...brandCollapsibleOptions}
            trigger={t(dict.PRICE_RANGE)}
          >
            {this.buildRange()}
          </Collapsible>
          <Collapsible {...brandCollapsibleOptions} trigger={t(dict.BRANDS)}>
            {this.buildBrands()}
          </Collapsible>
        </div>
        <Ctas
          onClickClear={this.onClickClear}
          onClickShowResults={this.onClickShowResults}
          t={t}
        />
      </Modal>
    );
  }
}

FiltersModal.defaultProps = {
  closeModal: () => {},
  isModalOpen: false,
  filters: {},
  selectedFilters: () => {},
  t: key => `dict-${key}`,
};

export default withConsumer(
  RichFilteringConsumer,
  'richFilteringContext',
)(FiltersModal);
